import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { QUALITY_INFO, type Color, type GameEvent, type GameResult, type MoveQuality, type MoveRecord, type MoveReview } from '@ic/engine';
import { FREE_GAMES, useGuestQuota } from '../lib/guest';
import { Icon, type IconName } from './Icons';

/** Per-move accuracy from the drop in winning chances (the curve lichess uses), averaged per side. */
function accuracy(reviews: Record<number, MoveReview>, moves: MoveRecord[], color: Color): number | null {
  const accs: number[] = [];
  for (const [n, r] of Object.entries(reviews)) {
    if (moves[Number(n) - 1]?.color !== color || r.quality === 'forced') continue;
    const drop = Math.max(0, r.winBest - r.winPlayed);
    accs.push(Math.max(0, Math.min(100, 103.1668 * Math.exp(-0.04354 * drop) - 3.1669)));
  }
  return accs.length ? accs.reduce((a, b) => a + b, 0) / accs.length : null;
}

const COUNTED: MoveQuality[] = ['brilliant', 'great', 'best', 'inaccuracy', 'mistake', 'blunder'];

/** The custom-piece moments worth bragging about, counted from the game's events. */
const HIGHLIGHTS: { e: GameEvent['e']; icon: IconName; one: string; many: string }[] = [
  { e: 'explode', icon: 'detonate', one: 'explosion', many: 'explosions' },
  { e: 'fire', icon: 'fireBreath', one: 'fire breath', many: 'fire breaths' },
  { e: 'sweep', icon: 'wingSweep', one: 'wing sweep', many: 'wing sweeps' },
  { e: 'mineTrigger', icon: 'placeMine', one: 'mine went off', many: 'mines went off' },
  { e: 'defuse', icon: 'defuse', one: 'mine defused', many: 'mines defused' },
  { e: 'tunnel', icon: 'tunnel', one: 'tunnel', many: 'tunnels' },
  { e: 'tongue', icon: 'tongue', one: 'frog tongue', many: 'frog tongues' },
  { e: 'hedge', icon: 'plantHedge', one: 'hedge planted', many: 'hedges planted' },
  { e: 'stampede', icon: 'stampede', one: 'stampede', many: 'stampedes' },
  { e: 'trumpet', icon: 'trumpet', one: 'trumpet blast', many: 'trumpet blasts' },
  { e: 'survive', icon: 'shuffle', one: 'cockroach escape', many: 'cockroach escapes' },
  { e: 'spawn', icon: 'multiply', one: 'piece spawned', many: 'pieces spawned' },
  { e: 'feast', icon: 'feast', one: 'feast', many: 'feasts' },
  { e: 'pan', icon: 'panToss', one: 'pan toss', many: 'pan tosses' },
  { e: 'promote', icon: 'crown', one: 'promotion', many: 'promotions' },
];

function highlights(log: GameEvent[]): { icon: IconName; text: string }[] {
  const out: { icon: IconName; text: string }[] = [];
  const destroyed = log.filter((e) => e.e === 'destroy').length;
  if (destroyed) out.push({ icon: 'x', text: `${destroyed} piece${destroyed > 1 ? 's' : ''} destroyed` });
  for (const h of HIGHLIGHTS) {
    const n = log.filter((e) => e.e === h.e).length;
    if (n) out.push({ icon: h.icon, text: `${n} ${n > 1 ? h.many : h.one}` });
  }
  return out;
}

export interface RecapNext { name: string; desc: string; onPlay: () => void }

export interface GameRecapProps {
  headline: string;
  result: GameResult;
  moves: MoveRecord[];
  reviews?: Record<number, MoveReview>;
  names: Record<Color, string>;
  /** The side this player had (bot and online games); null for pass & play. */
  viewer?: Color | null;
  eventLog?: GameEvent[];
  rulesetName: string;
  actions: ReactNode;
  next?: RecapNext | null;
  /** Jump the board to the position after move n. */
  onShow: (n: number) => void;
  onDismiss: () => void;
}

export function GameRecap(p: GameRecapProps) {
  const reviews = p.reviews ?? {};
  const here = useLocation();
  const quota = useGuestQuota();
  const [shared, setShared] = useState<'idle' | 'copied'>('idle');
  const sides: Color[] = p.viewer === 'b' ? ['b', 'w'] : ['w', 'b'];
  const acc = { w: accuracy(reviews, p.moves, 'w'), b: accuracy(reviews, p.moves, 'b') };
  const reviewed = Object.keys(reviews).length > 0;
  const count = (c: Color, q: MoveQuality) => Object.entries(reviews).filter(([n, r]) => r.quality === q && p.moves[Number(n) - 1]?.color === c).length;
  const shownQualities = COUNTED.filter((q) => sides.some((c) => count(c, q) > 0));

  // The move that swung the game most, and the finest move played.
  const entries = Object.entries(reviews).map(([n, r]) => ({ n: Number(n), r }));
  const turning = entries
    .filter(({ r }) => r.quality === 'blunder' || r.quality === 'mistake' || r.quality === 'miss')
    .sort((a, b) => (b.r.winBest - b.r.winPlayed) - (a.r.winBest - a.r.winPlayed))[0];
  const star = entries
    .filter(({ r }) => r.quality === 'brilliant' || r.quality === 'great')
    .sort((a, b) => (a.r.quality === b.r.quality ? b.n - a.n : a.r.quality === 'brilliant' ? -1 : 1))[0];
  const moveLabel = (n: number) => `${Math.ceil(n / 2)}${p.moves[n - 1]?.color === 'b' ? '…' : '.'} ${p.moves[n - 1]?.san ?? ''}`;
  const who = (n: number) => { const c = p.moves[n - 1]?.color; return c ? (c === p.viewer ? 'You' : p.names[c]) : ''; };

  const hl = highlights(p.eventLog ?? []);
  const turns = Math.ceil(p.moves.length / 2);

  async function share() {
    const mine = p.viewer ? acc[p.viewer] : null;
    const text = [
      `${p.headline} by ${p.result.reason} in ${turns} move${turns === 1 ? '' : 's'} on IllegalChess (${p.rulesetName}).`,
      mine != null ? `Accuracy ${Math.round(mine)}%.` : '',
      hl.length ? `${hl.slice(0, 3).map((h) => h.text).join(', ')}.` : '',
    ].filter(Boolean).join(' ');
    const url = location.origin;
    try {
      if (navigator.share) { await navigator.share({ title: 'IllegalChess', text, url }); return; }
      await navigator.clipboard.writeText(`${text} ${url}`);
      setShared('copied');
      setTimeout(() => setShared('idle'), 2200);
    } catch { /* share sheet closed */ }
  }

  return (
    <div className="result-card recap" aria-labelledby="recap-title">
      <div className="recap-head">
        <h2 id="recap-title">{p.headline}</h2>
        <p className="muted">by {p.result.reason} · {turns} move{turns === 1 ? '' : 's'} · {p.rulesetName}</p>
      </div>

      {reviewed && (
        <div className="recap-score">
          {sides.map((c) => (
            <div key={c} className={`recap-side ${c === p.viewer ? 'me' : ''}`}>
              <span className="recap-name"><span className={`turn-dot ${c}`} />{c === p.viewer ? 'You' : p.names[c]}</span>
              <span className="recap-acc">{acc[c] == null ? '–' : `${Math.round(acc[c]!)}%`}</span>
              <span className="recap-bar"><i style={{ width: `${acc[c] ?? 0}%` }} /></span>
              <span className="muted small">accuracy</span>
            </div>
          ))}
          {shownQualities.length > 0 && (
            <div className="recap-counts">
              {shownQualities.map((q) => (
                <div key={q} className="recap-count-row">
                  <span className="recap-n">{count(sides[0], q)}</span>
                  <span className="recap-q"><span className={`q-badge q-${q}`} style={{ background: QUALITY_INFO[q].color }}>{QUALITY_INFO[q].symbol}</span>{QUALITY_INFO[q].label}</span>
                  <span className="recap-n">{count(sides[1], q)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(turning || star) && (
        <div className="recap-moments">
          {star && (
            <button type="button" className="recap-moment" onClick={() => p.onShow(star.n)}>
              <span className={`q-badge q-${star.r.quality}`} style={{ background: QUALITY_INFO[star.r.quality].color }}>{QUALITY_INFO[star.r.quality].symbol}</span>
              <span className="grow"><b>Move of the game</b><span className="muted small">{who(star.n)}: <span className="mono">{moveLabel(star.n)}</span></span></span>
              <span className="recap-show">Show</span>
            </button>
          )}
          {turning && (
            <button type="button" className="recap-moment" onClick={() => p.onShow(turning.n)}>
              <span className={`q-badge q-${turning.r.quality}`} style={{ background: QUALITY_INFO[turning.r.quality].color }}>{QUALITY_INFO[turning.r.quality].symbol}</span>
              <span className="grow"><b>Turning point</b><span className="muted small">{who(turning.n)}: <span className="mono">{moveLabel(turning.n)}</span> · {turning.r.note}</span></span>
              <span className="recap-show">Show</span>
            </button>
          )}
        </div>
      )}

      {hl.length > 0 && (
        <div className="recap-hl" aria-label="Highlights">
          {hl.map((h) => <span key={h.text} className="recap-chip"><Icon name={h.icon} size={14} />{h.text}</span>)}
        </div>
      )}

      <div className="recap-actions">
        {p.actions}
        <button type="button" className="btn secondary" onClick={share}><Icon name="link" size={16} /> {shared === 'copied' ? 'Copied' : 'Share'}</button>
        <button type="button" className="btn ghost" onClick={p.onDismiss}>View board</button>
      </div>

      {p.next && (
        <button type="button" className="recap-next" onClick={p.next.onPlay}>
          <span className="grow"><span className="muted small">Try something new</span><b>{p.next.name}</b><span className="muted small">{p.next.desc}</span></span>
          <Icon name="arrow" size={18} />
        </button>
      )}

      {quota.guest && (
        <div className="recap-guest">
          <div className="grow">
            <b>Keep this game.</b>
            <span className="muted small">
              A free account saves your history and gives you a rating and online play.
              {Number.isFinite(quota.left) && <> <span className="recap-pips" aria-label={`${quota.left} of ${FREE_GAMES} free games left`}>
                {Array.from({ length: FREE_GAMES }, (_, i) => <i key={i} className={i < quota.left ? 'on' : ''} />)}
              </span> {quota.left === 0 ? 'No free games left.' : `${quota.left} free game${quota.left === 1 ? '' : 's'} left.`}</>}
            </span>
          </div>
          <Link className="btn primary" to={`/login?mode=signup&next=${encodeURIComponent(here.pathname + here.search)}`}>Sign up free</Link>
        </div>
      )}
    </div>
  );
}
